import React, { useState, useEffect, useRef } from 'react';
import pipelineService from '../services/pipelineService';
import { hasPermission } from '../utils/permissions';

/**
 * CardDocuments — collaboration files attached to a pipeline card.
 */
const CardDocuments = ({ cardId, compact = false }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('resume');
  const [notes, setNotes] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { loadDocuments(); }, [cardId]);

  const loadDocuments = async () => {
    try {
      const docs = await pipelineService.getDocuments(cardId);
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) { setSelectedFile(file); setShowUpload(true); }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) { setSelectedFile(file); setShowUpload(true); }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('file_type', fileType);
      if (notes.trim()) formData.append('notes', notes.trim());
      const token = localStorage.getItem('authToken');
      await fetch(`/api/cards/${cardId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      setSelectedFile(null);
      setNotes('');
      setFileType('resume');
      setShowUpload(false);
      await loadDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await pipelineService.deleteDocument(documentId);
      await loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const openDoc = async (doc) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/cards/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { console.error('Error opening file:', err); alert('Failed to open file'); }
  };

  const downloadDoc = async (doc) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/cards/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { console.error('Error downloading file:', err); alert('Failed to download file'); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getTypeColor = (type) => {
    const colors = { resume: 'bg-blue-100 text-blue-700', write_up: 'bg-teal-100 text-teal-700', cover_letter: 'bg-purple-100 text-purple-700', reference: 'bg-amber-100 text-amber-700', other: 'bg-gray-100 text-gray-600' };
    return colors[type] || colors.other;
  };

  if (loading) return <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mx-auto"></div></div>;

  return (
    <div className="space-y-3" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700">Collaboration Files</p>
        {hasPermission('upload_files') && (
        <button onClick={() => { setShowUpload(!showUpload); setSelectedFile(null); }}
          className="text-xs px-2.5 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700">
          {showUpload ? 'Cancel' : '+ Upload'}
        </button>
        )}
      </div>

      {error && <p className="text-[10px] text-red-500">{error}</p>}

      {/* Compact upload area */}
      {(showUpload || isDragging) && (
        <div className={`border border-dashed rounded-lg p-3 space-y-2.5 transition-colors ${isDragging ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 bg-gray-50'}`}>
          {!selectedFile ? (
            <div className="text-center py-3">
              <svg className="w-6 h-6 text-gray-400 mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-[10px] text-gray-500 mb-2">Drag & drop or click to browse</p>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileInputChange} />
              <button onClick={() => fileInputRef.current?.click()} className="text-[10px] px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-600">
                Choose File
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 bg-white rounded border p-2">
                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{selectedFile.name}</p>
                  <p className="text-[10px] text-gray-400">{formatFileSize(selectedFile.size)}</p>
                </div>
                <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="p-0.5 text-gray-400 hover:text-red-500">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex gap-2">
                <select value={fileType} onChange={(e) => setFileType(e.target.value)} disabled={uploading}
                  className="flex-1 text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500">
                  <option value="resume">Resume/CV</option>
                  <option value="write_up">Write-Up</option>
                  <option value="cover_letter">Cover Letter</option>
                  <option value="reference">Reference</option>
                  <option value="other">Other</option>
                </select>
                <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
                  className="flex-1 text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500" disabled={uploading} />
              </div>
              <button onClick={handleUpload} disabled={uploading}
                className="w-full text-xs py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 font-medium">
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Documents list */}
      {documents.length === 0 ? (
        !showUpload && (
          <div className="text-center py-6">
            <svg className="w-7 h-7 text-gray-300 mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-xs text-gray-400">No collaboration files yet</p>
          </div>
        )
      ) : (
        <div className="space-y-1.5">
          {documents.map(doc => (
            <div key={doc.id} className="flex items-center gap-2 group border rounded-lg p-2 hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors">
              <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <button onClick={() => openDoc(doc)}
                  className="text-xs font-medium text-gray-800 hover:text-emerald-700 hover:underline truncate block text-left w-full">{doc.filename}</button>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${getTypeColor(doc.file_type)}`}>
                    {(doc.file_type || 'other').replace('_', ' ')}
                  </span>
                  <span className="text-[9px] text-gray-400">
                    {doc.uploaded_by_name ? `${doc.uploaded_by_name}` : ''}{doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                  </span>
                </div>
              </div>
              <button onClick={() => openDoc(doc)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-emerald-700 transition-opacity flex-shrink-0" title="Open in new tab">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
              <button onClick={() => downloadDoc(doc)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-green-600 transition-opacity flex-shrink-0" title="Download">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              <button onClick={() => handleDelete(doc.id, doc.filename)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity flex-shrink-0" title="Delete">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CardDocuments;
